import type { EIremboFormlyFieldTypes } from '../config/formly-configs';

// The one type a column may not use: a table cannot be nested inside a
// table. Kept as a literal (not the enum member) so this model stays a
// type-only dependency of the config that registers the component itself.
const NESTED_TABLE_TYPE = 'customtablewithinputform';

/**
 * The column's `type` is the platform's real, existing Formly field type
 * (not a custom abstraction) — the "Field type" dropdown in the config
 * wizard maps onto one of these, matching how every other field type
 * already registers with the form engine. Every type ui-common registers is
 * accepted except the table itself (see isTableWithInputColumnTypeAllowed);
 * the add/edit panel renders each column through that type's own component.
 */
export type TableWithInputColumnFieldType = Exclude<
  `${EIremboFormlyFieldTypes}`,
  typeof NESTED_TABLE_TYPE
>;

export const isTableWithInputColumnTypeAllowed = (
  type: string | undefined
): type is TableWithInputColumnFieldType =>
  !!type && type !== NESTED_TABLE_TYPE;

export type TableWithInputColumnOption = { label: string; value: any } & Record<
  string,
  unknown
>;

// Narrows a column's available dropdown options based on another column's
// CURRENT value in the same row (e.g. "Item description" choices depend on
// which "Name of item" was picked) — edit-time only, resolved fresh on every
// row-form change. Deliberately separate from `props.options`: that stays
// the column's full/combined option list, still used as-is for read-only
// table/summary display and label resolution (resolveTableWithInputSelect
// DisplayValues), so existing non-dependent dropdown columns — and every
// consumer of a saved row's value — see no change at all.
export interface TableWithInputDependentOptions {
  dependsOn: string;
  optionsByValue: Record<string, TableWithInputColumnOption[]>;
}

export interface TableWithInputColumnProps {
  label: string;
  type?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  // Inverted polarity vs. the wizard's "Show field in table" toggle
  // (Yes/No): a column is shown in the table by default, so this defaults
  // to false/absent, only becoming true when the SM explicitly hides it.
  hiddenColumn?: boolean;
  widthPercent?: number | null;
  unique?: boolean;
  // Extra keys beyond label/value are allowed so bindLabel/bindValue can
  // point at differently-named properties, matching custom-drop-down's
  // options shape.
  options?: TableWithInputColumnOption[];
  multiple?: boolean;
  bindValue?: string;
  bindLabel?: string;
  default?: boolean;
  pattern?: string;
  hideLabel?: boolean;
  description?: string;
  indeterminate?: boolean;
  // Date columns forward the rest of their props as-is to
  // irembogov-irembo-date-picker (maxDate, minDate, dateFormat, delimeter,
  // eventCode, eventDatesMaxSize, addRemoveTimeMinDate, addRemoveTimeMaxDate,
  // allowedDaysOfTheWeek, excludedDates), matching what the existing
  // customdatepicker Formly wrapper already supports — loosened to `any`
  // (from `unknown`) so those bindings type-check the same way that
  // wrapper's own `props: Record<string, any>` does.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface TableWithInputColumn {
  key: string;
  type: TableWithInputColumnFieldType;
  className?: string;
  props: TableWithInputColumnProps;
  // Mirrors formly's own expressionProperties convention — each key is
  // "model.<targetColumnKey>", each value a JS expression string evaluated
  // against the row's current raw values (exposed as `model`) whenever any
  // field in the row changes. Only "model.*" targets are supported; a target
  // that isn't itself a configured column key is ignored.
  // The one other key read is `hide`: an expression (same `model` scope)
  // that, while truthy, hides this column's field in the add/edit panel.
  // A hidden column is also cleared and exempt from validation (so a hidden
  // `required` column can't block saving), and saves as empty.
  // The other is `props.required`: an expression that replaces the column's
  // static `props.required`, re-evaluated with the row as `model` and the
  // column as `field` (`field.props`, `field.hide`) — the scope formly's own
  // `props.required` expression gets, so a config written for any other
  // field type works unchanged.
  expressions?: Record<string, string>;
  // See TableWithInputDependentOptions above.
  dependentOptions?: TableWithInputDependentOptions;
  // Wording shown under the column's field in the add/edit panel, keyed by
  // validation error name: `required`, `email`, `pattern`, `minlength`, or
  // any error a validator of the column's type reports. `unique` is this
  // component's own row-level check. An error with no entry falls back to
  // the panel's defaults, then to the message registered with formly.
  validation?: {
    messages?: {
      required?: string;
      unique?: string;
      [key: string]: string | undefined;
    };
  };
}

// Composite uniqueness across two columns together (e.g. firstName+lastName)
// — distinct from a single column's own `props.unique`, which only checks
// that one field in isolation. Off unless `enabled` is explicitly set.
export interface TableWithInputUniqueFieldsCombination {
  enabled: boolean;
  fields: [string, string];
  message?: string;
}

export type TableWithInputRow = Record<string, unknown> & { __rowId: string };

export type TableWithInputColumnKind =
  | 'text'
  | 'number'
  | 'email'
  | 'date'
  | 'select'
  | 'checkbox';

// Shared by the live field (custom-table-with-input) and the read-only
// application summary/review preview (application-form.util) so both agree
// on how a column's stored value should be interpreted and displayed.
// Types whose stored value is one of (or, for `multiple`, several of) the
// column's configured options, so display resolves through those options'
// labels rather than showing the raw bound value.
const OPTION_BACKED_TYPES: ReadonlySet<string> = new Set([
  'select',
  'radio',
  'customdropdown',
  'customdropdownpaginated',
  'customdropdowndatafetchpaginated',
  'custommultiselectdatafetch',
]);

export const resolveTableWithInputColumnKind = (
  column: TableWithInputColumn
): TableWithInputColumnKind => {
  if (
    column.type === 'customdatepicker' ||
    column.type === 'customdatetimepicker'
  ) {
    return 'date';
  }
  if (OPTION_BACKED_TYPES.has(column.type)) {
    return 'select';
  }
  if (column.type === 'checkbox') {
    return 'checkbox';
  }
  if (column.props?.type === 'number') {
    return 'number';
  }
  if (column.props?.type === 'email') {
    return 'email';
  }
  return 'text';
};

// Column options honor a custom bindValue/bindLabel (matching
// custom-drop-down's ng-select convention) instead of always assuming the
// option shape is exactly {label, value} — shared so the live field and the
// read-only preview builder resolve an option's value/label identically.
export const getTableWithInputOptionValue = (
  column: TableWithInputColumn,
  option: Record<string, unknown>
): unknown => option[column.props?.bindValue ?? 'value'];

const stringifyCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

export const getTableWithInputOptionLabel = (
  column: TableWithInputColumn,
  option: Record<string, unknown>
): string => stringifyCellValue(option[column.props?.bindLabel ?? 'label']);

// Resolves a stored cell value (or, for a `multiple` column, each value in
// the stored array) to its configured option's label, honoring
// bindValue/bindLabel. Falls back to the raw value when no matching option
// is found so unexpected/legacy data doesn't just disappear.
export const resolveTableWithInputSelectDisplayValues = (
  column: TableWithInputColumn,
  value: unknown
): string[] => {
  const options = column.props?.options ?? [];
  let rawValues: unknown[];
  if (column.props?.multiple) {
    rawValues = Array.isArray(value) ? value : [];
  } else {
    rawValues = [value];
  }

  return rawValues.map(rawValue => {
    const option = options.find(
      opt => getTableWithInputOptionValue(column, opt) === rawValue
    );
    return option
      ? getTableWithInputOptionLabel(column, option)
      : stringifyCellValue(rawValue);
  });
};
